import {
	IEditMetadataDiff,
	IEditMetadataRow,
	IEditMetadataCategory,
} from "src/pages/EditMetadata/models/interfaces";

const trim = (value: unknown) => String(value || "").trim();

const dedupe = (values: string[]): string[] => {
	const seen = new Set<string>();
	const out: string[] = [];
	for (const value of values) {
		const cleaned = trim(value);
		if (!cleaned || seen.has(cleaned)) continue;
		seen.add(cleaned);
		out.push(cleaned);
	}
	return out;
};

// Build a Map<file_id, Map<category, Set<value>>> from a flat row list. Used
// both for the "current" snapshot and the "edited" snapshot when computing
// the dirty diff.
const indexRows = (rows: IEditMetadataRow[]) => {
	const out = new Map<string, Map<string, Set<string>>>();
	for (const row of rows || []) {
		const fileId = trim(row?.file_id);
		if (!fileId) continue;
		const perCategory = new Map<string, Set<string>>();
		for (const tag of row.tags || []) {
			const category = trim(tag?.tag_category);
			const value = trim(tag?.tag_value);
			if (!category || !value) continue;
			if (!perCategory.has(category)) perCategory.set(category, new Set());
			perCategory.get(category)!.add(value);
		}
		out.set(fileId, perCategory);
	}
	return out;
};

const setsEqual = (a: Set<string>, b: Set<string>): boolean => {
	if (a.size !== b.size) return false;
	for (const v of a) if (!b.has(v)) return false;
	return true;
};

// Compute the staged ops between an original snapshot and the current edited
// rows. Only emits cell_edits — global ops (renames, deletes, find/replace)
// are tracked separately as the user performs them, since after they're
// applied to the row state the diff would otherwise show as a flood of
// per-cell edits.
//
// Callers therefore pass: the original rows AND the staged global op lists.
// We walk only the rows to detect cell-level dirty cells, then merge with
// the global ops the caller is already tracking.
export interface IDiffArgs {
	originalRows: IEditMetadataRow[];
	currentRows: IEditMetadataRow[];
	categoryRenames?: IEditMetadataDiff["category_renames"];
	categoryDeletes?: IEditMetadataDiff["category_deletes"];
	valueRenames?: IEditMetadataDiff["value_renames"];
	valueDeletes?: IEditMetadataDiff["value_deletes"];
}

export const computeDiff = ({
	categoryDeletes = [],
	categoryRenames = [],
	currentRows,
	originalRows,
	valueDeletes = [],
	valueRenames = [],
}: IDiffArgs): IEditMetadataDiff => {
	const originalIndex = indexRows(originalRows);
	const currentIndex = indexRows(currentRows);

	// Build the "expected" state by applying all global ops to the original
	// snapshot. Anything in currentIndex that differs from this expected state
	// after global-op application becomes a cell_edit.
	const expectedIndex = new Map<string, Map<string, Set<string>>>();
	for (const [fileId, perCat] of originalIndex.entries()) {
		const cloned = new Map<string, Set<string>>();
		for (const [cat, vals] of perCat.entries()) cloned.set(cat, new Set(vals));
		expectedIndex.set(fileId, cloned);
	}

	for (const rename of categoryRenames) {
		const src = trim(rename.from_category);
		const dst = trim(rename.to_category);
		if (!src || !dst || src === dst) continue;
		for (const perCat of expectedIndex.values()) {
			if (perCat.has(src)) {
				const merged = perCat.get(dst) || new Set<string>();
				perCat.get(src)!.forEach((v) => merged.add(v));
				perCat.set(dst, merged);
				perCat.delete(src);
			}
		}
	}
	for (const category of categoryDeletes) {
		const cat = trim(category);
		if (!cat) continue;
		for (const perCat of expectedIndex.values()) perCat.delete(cat);
	}
	for (const rename of valueRenames) {
		const cat = trim(rename.tag_category);
		const from = trim(rename.from_value);
		const to = trim(rename.to_value);
		if (!cat || !from || !to || from === to) continue;
		for (const perCat of expectedIndex.values()) {
			const values = perCat.get(cat);
			if (values && values.has(from)) {
				values.delete(from);
				values.add(to);
			}
		}
	}
	for (const del of valueDeletes) {
		const cat = trim(del.tag_category);
		const value = trim(del.value);
		if (!cat || !value) continue;
		for (const perCat of expectedIndex.values()) {
			const values = perCat.get(cat);
			if (values && values.has(value)) {
				values.delete(value);
				if (!values.size) perCat.delete(cat);
			}
		}
	}

	// Diff currentIndex vs expectedIndex per (file × category).
	const cellEdits: IEditMetadataDiff["cell_edits"] = [];
	const allFileIds = new Set<string>([...expectedIndex.keys(), ...currentIndex.keys()]);
	for (const fileId of allFileIds) {
		const expectedCats = expectedIndex.get(fileId) || new Map();
		const currentCats = currentIndex.get(fileId) || new Map();
		const allCats = new Set<string>([...expectedCats.keys(), ...currentCats.keys()]);
		for (const category of allCats) {
			const expectedValues = expectedCats.get(category) || new Set<string>();
			const currentValues = currentCats.get(category) || new Set<string>();
			if (!setsEqual(expectedValues, currentValues)) {
				cellEdits.push({
					file_id: fileId,
					tag_category: category,
					new_values: [...currentValues].sort(),
				});
			}
		}
	}

	return {
		cell_edits: cellEdits,
		category_renames: categoryRenames,
		category_deletes: categoryDeletes,
		value_renames: valueRenames,
		value_deletes: valueDeletes,
	};
};

export const isDiffEmpty = (diff: IEditMetadataDiff): boolean =>
	!diff.cell_edits.length &&
	!diff.category_renames.length &&
	!diff.category_deletes.length &&
	!diff.value_renames.length &&
	!diff.value_deletes.length;

export const buildCategoriesFromRows = (
	rows: IEditMetadataRow[],
	preferredOrder: string[] = [],
): IEditMetadataCategory[] => {
	const inventory = new Map<string, Map<string, number>>();
	const discoveredOrder: string[] = [];
	for (const row of rows || []) {
		for (const tag of row.tags || []) {
			const category = trim(tag?.tag_category);
			const value = trim(tag?.tag_value);
			if (!category || !value) continue;
			if (!inventory.has(category)) {
				inventory.set(category, new Map());
				discoveredOrder.push(category);
			}
			const counts = inventory.get(category)!;
			counts.set(value, (counts.get(value) || 0) + 1);
		}
	}

	const seen = new Set<string>();
	const order: string[] = [];
	const appendCategory = (categoryValue: string) => {
		const category = trim(categoryValue);
		if (!category || seen.has(category)) return;
		seen.add(category);
		order.push(category);
	};

	for (const category of preferredOrder) appendCategory(category);
	for (const category of discoveredOrder) appendCategory(category);

	return order.map((category) => ({
		name: category,
		values: [...(inventory.get(category) || new Map<string, number>()).entries()]
			.map(([value, count]) => ({ value, count }))
			.sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase())),
	}));
};

export const dedupeValues = dedupe;

/**
 * Case-insensitive duplicate check for category names.
 * Called by both AddCategoryModal (pre-submit validation) and the addCategory
 * reducer (authoritative guard) so the two sites can never diverge (issue #14).
 */
export const isCategoryNameTaken = (
	name: string,
	rows: IEditMetadataRow[],
	pendingCategoryAdds: string[],
): boolean => {
	const lower = name.toLowerCase();
	const existing = new Set<string>([
		...pendingCategoryAdds.map((c) => c.toLowerCase()),
		...rows.flatMap((r) => r.tags.map((tg) => tg.tag_category.toLowerCase())),
	]);
	return existing.has(lower);
};
